import { Card, Modal, Spin } from "antd";
import React, { useState, useEffect } from "react";
import { changeDateAndTimeFormat } from "../../utils/commonFunctions";
import useAxios from "../../hooks/useAxios";

function getVLevel(score) {
  if (score >= 71) return "V6"; // C1+ / C2
  else if (score >= 60) return "V5"; // C1
  else if (score >= 50) return "V4"; // B2
  else if (score >= 40) return "V3"; // B1
  else if (score >= 30) return "V2"; // A2
  else if (score >= 20) return "V1"; // A1
  else return "V1"; // Below CEFR A1
}

const rows = [
  {
    key: "overall",
    value: "Overall Score",
  },
  {
    key: "fluency",
    value: "Fluency",
  },
  {
    key: "vocabulary",
    value: "Vocabulary",
  },
  {
    key: "pronunciation",
    value: "Pronunciation",
  },
  {
    key: "grammar",
    value: "Grammar",
  },
];

const LanguageConfidence = ({ candidate }) => {
  const [expanded, setExpanded] = useState(false);
  function toggleExpanded(e) {
    setExpanded(!expanded);
  }

  const api = useAxios();

  const [loadingAudios, setLoadingAudios] = useState(false);
  const [playableAudios, setPlayableAudios] = useState({}); // Stores URLs by index: { 0: "url1", 1: "url2" }

  const data = {
    success: true,
    message: "Test results saved successfully",
    data: {
      pronunciation: {
        overall_score: 32.2,
        english_proficiency_scores: {
          mock_ielts: {
            prediction: 3.5,
          },
          mock_cefr: {
            prediction: "A1",
          },
          mock_pte: {
            prediction: 15,
          },
        },
      },
      fluency: {
        overall_score: 100,
        english_proficiency_scores: {
          mock_ielts: {
            prediction: 9,
          },
          mock_cefr: {
            prediction: "C2",
          },
          mock_pte: {
            prediction: 89,
          },
        },
      },
      vocabulary: {
        overall_score: 43,
        english_proficiency_scores: {
          mock_ielts: {
            prediction: 4.5,
          },
          mock_cefr: {
            prediction: "B1",
          },
          mock_pte: {
            prediction: 23,
          },
        },
      },
      grammar: {
        overall_score: 70,
        english_proficiency_scores: {},
      },
      overall: {
        overall_score: 61.5,
        english_proficiency_scores: {
          mock_ielts: {
            prediction: 6,
          },
          mock_cefr: {
            prediction: "B2",
          },
          mock_pte: {
            prediction: 46,
          },
        },
      },
      total_questions: 1,
      testId: "69099278f60fc88f10350a91",
    },
  };

  const audioFiles = candidate?.languageConfidenceScore?.audioFiles || [];
  const testResults = candidate?.languageConfidenceScore?.testResults || [];

  // Helper: get question text for an audio file, with fallback to testResults
  const getQuestionText = (audioFile, index) => {
    if (audioFile?.questionText) return audioFile.questionText;
    const matched = testResults.find(
      (tr) => tr.questionIndex === audioFile?.questionIndex
    );
    if (matched?.questionText) return matched.questionText;
    return `Question ${(audioFile?.questionIndex ?? index) + 1}`;
  };

  const [currentAudioIndex, setCurrentAudioIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showAudioModal, setShowAudioModal] = useState(false);
  const audioRef = React.useRef(null);

  const handleNext = () => {
    if (currentAudioIndex < audioFiles.length - 1) {
      setCurrentAudioIndex(currentAudioIndex + 1);
      setIsPlaying(false);
    }
  };

  const handleBack = () => {
    if (currentAudioIndex > 0) {
      setCurrentAudioIndex(currentAudioIndex - 1);
      setIsPlaying(false);
    }
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
  };

  const openAudioModal = () => {
    setCurrentAudioIndex(0);
    setShowAudioModal(true);
  };

  const closeAudioModal = () => {
    setShowAudioModal(false);
    setIsPlaying(false);
    setCurrentAudioIndex(0);
  };

  useEffect(() => {
    const fetchSignedUrl = async () => {
      const file = audioFiles[currentAudioIndex];
      if (showAudioModal && file && !playableAudios[currentAudioIndex]) {
        setLoadingAudios(true);
        try {
          const response = await api.post('/aws/awsFile', { path: file.s3Url }, { tokenType: 'user' });
          const url = response.data;
          
          setPlayableAudios(prev => ({
            ...prev,
            [currentAudioIndex]: url
          }));
        } catch (error) {
          console.error("Error fetching audio", error);
        } finally {
          setLoadingAudios(false);
        }
      }
    };

    fetchSignedUrl();
  }, [showAudioModal, currentAudioIndex, audioFiles]);

  const isTestDone =
    candidate?.languageConfidenceScore?.status === 'completed';

  const languageData = candidate?.languageConfidenceScore?.overallScores || {};
  const _score = languageData['overall']?.overall_score;
  const _gradeData = getVLevel(_score);
  const testDate = candidate?.languageConfidenceScore?.createdAt

  return (
    <Card bodyStyle={{ padding: "0 24px" }}>
      <div className="card my-2 border-0">
        <div className="card-header flex justify-content-between gap-2 p-2 card-custom">
          <div className="flex py-2 justify-content-between gap-3">
            <div>
              <i
                className="fa fa-calendar"
                aria-hidden="true"
                style={{ fontSize: 40 }}
              ></i>
            </div>
            <div>
              <span className="d-flex gap-1">
                <strong>Communication Assessment</strong>{" "}
                {isTestDone ? (
                  <span
                    className="px-2"
                    style={{
                      fontSize: "12px",
                      border: `1px solid green`,
                      borderRadius: "10px",
                      color: "green",
                      fontWeight: '700'
                    }}
                  >
                    {_gradeData} ({_score})
                  </span>
                ) : (
                  <span
                    className="px-2"
                    style={{
                      fontSize: "12px",
                      border: "1px solid red",
                      borderRadius: "10px",
                      color: "red",
                      fontWeight: '700'
                    }}
                  >
                    Pending
                  </span>
                )}
              </span>
              <span> {testDate ? changeDateAndTimeFormat(testDate) : 'NA'}</span>
            </div>
          </div>
          <div className="InterviewActBtns flex justify-content-end align-items-center">
            <button className="btn btn-primary btn-sm" onClick={toggleExpanded}>
              <i
                className={
                  expanded ? "fa fa-minus-circle" : "fa fa-plus-circle"
                }
              ></i>
            </button>
          </div>
        </div>

        <div
          className={
            expanded
              ? "card-body-animated"
              : "card-body-animated card-body-hidden"
          }
        >
          <div className="card-body px-2 py-0">
            <table className="table table-candidate">
              <tbody>
                {rows.map((item) => {
                  const score = languageData[item.key]?.overall_score;
                  const gradeData = getVLevel(score);
                  return (
                    <tr key={item.key}>
                      <td width={"149px"}>
                        <strong>{item.value}</strong>
                      </td>
                      <td>
                        <span
                          style={{
                            fontWeight: "500",
                            color: isTestDone ? gradeData?.color : "#5a5c69",
                          }}
                        >
                          {isTestDone ? `${score}` : "NA"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Play Audios Button */}
            {isTestDone && audioFiles.length > 0 && (
              <div className="d-flex flex-column align-items-start mt-3 pb-2 border-top pt-2">
                <button
                  className="btn btn-sm btn-outline-primary"
                  onClick={openAudioModal}
                >
                  <i className="fa fa-play-circle mr-1"></i> Play Audios
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Audio Modal Popup */}
      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div style={{
              backgroundColor: "#e6f7ff",
              borderRadius: "8px",
              width: "32px",
              height: "32px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}>
              <i className="fa fa-headphones" style={{ color: "#1890ff", fontSize: "16px" }}></i>
            </div>
            <span style={{ fontSize: "16px", fontWeight: "600" }}>Audio Recordings</span>
          </div>
        }
        open={showAudioModal}
        onCancel={closeAudioModal}
        footer={null}
        centered
        destroyOnClose
        width={500}
        maskStyle={{ backdropFilter: "blur(6px)", backgroundColor: "rgba(0, 0, 0, 0.45)" }}
        bodyStyle={{ padding: "20px 24px" }}
      >
        {audioFiles.length > 0 && (
          <div>
            {/* Progress Bar */}
            <div style={{ marginBottom: "16px" }}>
              <div style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "6px",
              }}>
                <span style={{ fontSize: "12px", color: "#8c8c8c" }}>Progress</span>
                <span style={{ fontSize: "12px", color: "#1890ff", fontWeight: "600" }}>
                  {currentAudioIndex + 1} of {audioFiles.length}
                </span>
              </div>
              <div style={{
                height: "4px",
                backgroundColor: "#f0f0f0",
                borderRadius: "2px",
                overflow: "hidden",
              }}>
                <div style={{
                  height: "100%",
                  width: `${((currentAudioIndex + 1) / audioFiles.length) * 100}%`,
                  backgroundColor: "#1890ff",
                  borderRadius: "2px",
                  transition: "width 0.3s ease",
                }} />
              </div>
            </div>

            {/* Question Card */}
            <div
              style={{
                backgroundColor: "#f6f8fc",
                borderRadius: "8px",
                padding: "14px 16px",
                marginBottom: "16px",
                borderLeft: "4px solid #1890ff",
                boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
              }}
            >
              <span
                style={{
                  fontSize: "11px",
                  color: "#1890ff",
                  fontWeight: "700",
                  textTransform: "uppercase",
                  letterSpacing: "0.8px",
                }}
              >
                Question {(audioFiles[currentAudioIndex]?.questionIndex ?? currentAudioIndex) + 1}
              </span>
              <div
                style={{
                  fontSize: "14px",
                  color: "#262626",
                  fontWeight: "500",
                  marginTop: "6px",
                  lineHeight: "1.5",
                }}
              >
                {getQuestionText(audioFiles[currentAudioIndex], currentAudioIndex)}
              </div>
            </div>

            {/* Audio Player */}
            <div
              style={{
                backgroundColor: "#fafafa",
                borderRadius: "8px",
                padding: "14px",
                border: "1px solid #f0f0f0",
              }}
            >
              {loadingAudios ? (
                <div className="d-flex align-items-center justify-content-center p-3">
                  <Spin size="small" />
                  <span className="ml-2 text-muted" style={{ fontSize: '12px' }}>Loading audio...</span>
                </div>
              ) : playableAudios[currentAudioIndex] ? (
                <audio
                  ref={audioRef}
                  key={playableAudios[currentAudioIndex]}
                  src={playableAudios[currentAudioIndex]}
                  onEnded={handleAudioEnded}
                  onPlay={() => setIsPlaying(true)}
                  onPause={() => setIsPlaying(false)}
                  controls
                  controlsList="nodownload"
                  style={{ width: '100%', outline: 'none' }}
                />
              ) : (
                <div className="text-center p-2">
                  <span className="text-muted" style={{ fontSize: '12px' }}>Failed to load audio</span>
                </div>
              )}
            </div>

            {/* Navigation Controls */}
            <div
              className="d-flex align-items-center justify-content-between"
              style={{ marginTop: "20px" }}
            >
              <button
                className="btn btn-sm"
                onClick={handleBack}
                disabled={currentAudioIndex === 0}
                style={{
                  border: "1px solid #d9d9d9",
                  borderRadius: "6px",
                  padding: "6px 16px",
                  fontWeight: "500",
                  color: currentAudioIndex === 0 ? "#bfbfbf" : "#595959",
                  backgroundColor: currentAudioIndex === 0 ? "#f5f5f5" : "#fff",
                  cursor: currentAudioIndex === 0 ? "not-allowed" : "pointer",
                }}
              >
                <i className="fa fa-chevron-left mr-1" style={{ fontSize: "10px" }}></i> Previous
              </button>
              <button
                className="btn btn-sm"
                onClick={handleNext}
                disabled={currentAudioIndex === audioFiles.length - 1}
                style={{
                  border: "none",
                  borderRadius: "6px",
                  padding: "6px 16px",
                  fontWeight: "500",
                  color: currentAudioIndex === audioFiles.length - 1 ? "#bfbfbf" : "#fff",
                  backgroundColor: currentAudioIndex === audioFiles.length - 1 ? "#f5f5f5" : "#1890ff",
                  cursor: currentAudioIndex === audioFiles.length - 1 ? "not-allowed" : "pointer",
                }}
              >
                Next <i className="fa fa-chevron-right ml-1" style={{ fontSize: "10px" }}></i>
              </button>
            </div>
          </div>
        )}
      </Modal>
    </Card>
  );
};

export default LanguageConfidence;
